import React, { useCallback, useEffect, useRef, useState } from 'react';

import classNames from 'classnames';

import AdminToolbar from '../admin/admin_toolbar';
import AssistantHeader from './header';
import { Card } from '@buildoutinc/blueprint-react/ui/Card';
import { Conversation } from '../conversation';
import { ConversationProvider, useConversationContext } from '../conversation_context';
import { getPageContext } from '../hooks/use_assistant';
import { DropOverlay } from '../drop_overlay';
import {
  CLOSE_ASSISTANT_EVENT,
  NEXT_BRIEF_OFFERED_EVENT,
  OPEN_ASSISTANT_EVENT,
  openAssistant,
  TOGGLE_ASSISTANT_EVENT,
  type NextBriefOffer,
  type OpenAssistantOptions,
} from '../events';
import { NextActionsProvider, useNextActionsContext, withNextActions } from '../next_actions_context';
import { PinnedQuestions } from '../tools/inline/question_cards/pinned';
import { PinnedRecommendations } from '../tools/inline/recommendation_cards';
import { StandaloneTools } from '../tools/standalone';
import { ToolsProvider } from '../tools/context';
import { readOpenState, writeOpenState } from './open_state';
import { useRailLayout } from './use_rail_layout';

import type { Agent, Paths } from '../types';

interface Props {
  agents: Agent[];
  currentUser: {
    admin: boolean;
    firstName?: string | null;
  };
  paths: Paths;
}

export type PanelMode = 'closed' | 'rail' | 'fullscreen';

export default function AiAssistant({ agents, currentUser, paths }: Props) {
  const [open, setOpen] = useState(readOpenState);
  const [fullscreen, setFullscreen] = useState(false);

  const mode: PanelMode = !open ? 'closed' : fullscreen ? 'fullscreen' : 'rail';

  useRailLayout(mode !== 'closed');

  function changeOpen(next: boolean) {
    setOpen(next);
    writeOpenState(next);
    if (!next) {
      setFullscreen(false);
    }
  }

  return (
    <ToolsProvider>
      <NextActionsProvider>
        <AssistantConversation
          agents={agents}
          currentUser={currentUser}
          mode={mode}
          paths={paths}
          onChangeFullscreen={setFullscreen}
          onChangeOpen={changeOpen}
        />
      </NextActionsProvider>

      <StandaloneTools/>
    </ToolsProvider>
  );
}

function AssistantConversation({ agents, currentUser, mode, paths, onChangeFullscreen, onChangeOpen }: Props & {
  mode: PanelMode;
  onChangeFullscreen: (fullscreen: boolean) => void;
  onChangeOpen: (open: boolean) => void;
}) {
  const nextActions = useNextActionsContext();

  return (
    <ConversationProvider
      agents={agents}
      firstName={currentUser.firstName}
      isAdmin={currentUser.admin}
      pageContext={withNextActions(nextActions, getPageContext)}
      paths={paths}
      visible={mode !== 'closed'}
    >
      <SidebarPanel
        mode={mode}
        onChangeFullscreen={onChangeFullscreen}
        onClose={() => onChangeOpen(false)}
        onOpen={() => onChangeOpen(true)}
      />
    </ConversationProvider>
  );
}

interface PanelProps {
  mode: PanelMode;
  onChangeFullscreen: (fullscreen: boolean) => void;
  onClose: () => void;
  onOpen: () => void;
}

function SidebarPanel({ mode, onChangeFullscreen, onClose, onOpen }: PanelProps) {
  const { busy, getRootProps, isDragActive, say, send } = useConversationContext();
  const fullscreen = mode === 'fullscreen';
  const busyRef = useRef(busy);
  busyRef.current = busy;
  const heldOffer = useRef<NextBriefOffer | null>(null);

  const onOpenRef = useRef(onOpen);
  onOpenRef.current = onOpen;
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const modeRef = useRef(mode);
  modeRef.current = mode;

  useEffect(() => {
    const handler = (event: Event) => {
      const detail = (event as CustomEvent<OpenAssistantOptions>).detail;

      onOpenRef.current();
      if (detail?.prompt) {
        send(detail.prompt);
      }
    };

    window.addEventListener(OPEN_ASSISTANT_EVENT, handler);
    return () => window.removeEventListener(OPEN_ASSISTANT_EVENT, handler);
  }, [send]);

  const sayOffer = useCallback(({ done, next }: NextBriefOffer) => {
    if (next) {
      say(
        `${done} is off your list — nice work. Want me to brief you on ${next.name} next?`,
        () => openAssistant({ brief: next.brief, prompt: next.prompt })
      );
    } else {
      say(`${done} is off your list — nice work. That's everything for now.`);
    }
  }, [say]);

  useEffect(() => {
    const handler = (event: Event) => {
      const offer = (event as CustomEvent<NextBriefOffer>).detail;
      if (modeRef.current === 'closed') {
        return;
      }

      if (busyRef.current) {
        heldOffer.current = offer;
      } else {
        sayOffer(offer);
      }
    };

    window.addEventListener(NEXT_BRIEF_OFFERED_EVENT, handler);
    return () => window.removeEventListener(NEXT_BRIEF_OFFERED_EVENT, handler);
  }, [sayOffer]);

  useEffect(() => {
    const offer = heldOffer.current;
    if (busy || !offer) {
      return;
    }

    heldOffer.current = null;
    if (modeRef.current !== 'closed') {
      sayOffer(offer);
    }
  }, [busy, sayOffer]);

  useEffect(() => {
    const handler = () => onCloseRef.current();

    window.addEventListener(CLOSE_ASSISTANT_EVENT, handler);
    return () => window.removeEventListener(CLOSE_ASSISTANT_EVENT, handler);
  }, []);

  useEffect(() => {
    const handler = () => (modeRef.current === 'closed' ? onOpenRef.current() : onCloseRef.current());

    window.addEventListener(TOGGLE_ASSISTANT_EVENT, handler);
    return () => window.removeEventListener(TOGGLE_ASSISTANT_EVENT, handler);
  }, []);

  function handleKeyDown(event: React.KeyboardEvent<HTMLDivElement>) {
    if (event.key === 'Escape' && fullscreen) {
      onChangeFullscreen(false);
    }
  }

  return (
    // Kept mounted while collapsed so the transcript and any turn still streaming survive a close
    <div
      className={classNames('ai-assistant-panel', {
        'ai-assistant-panel--fullscreen': fullscreen,
        'd-none': mode === 'closed',
      })}
      onKeyDown={handleKeyDown}
    >
      <Card
        className="ai-assistant d-flex flex-column h-100 overflow-hidden"
        {...getRootProps()}
      >
        {isDragActive && <DropOverlay/>}

        <AssistantHeader
          fullscreen={fullscreen}
          onClose={onClose}
          onToggleFullscreen={() => onChangeFullscreen(!fullscreen)}
        />
        <AdminToolbar />
        <Conversation pinned={<><PinnedRecommendations /><PinnedQuestions /></>} />
      </Card>
    </div>
  );
}
